import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import '../../App.css';

const PERMISSIONS = [
  { key: 'manageTidbits', label: 'Manage Tidbits', description: 'Create, edit, and delete tidbits' },
  { key: 'manageRaces', label: 'Manage Races', description: 'Create, edit, and delete races; update horses' },
  { key: 'submitResults', label: 'Submit Results', description: 'Submit race results' },
  { key: 'approveUsers', label: 'Approve Users', description: 'Approve or reject user registrations' },
  { key: 'manageRegistrationCodes', label: 'Registration Codes', description: 'Create and manage registration codes' },
  { key: 'viewAdminStats', label: 'View Admin Stats', description: 'View the admin dashboard statistics' },
  { key: 'manageHorseRacing', label: 'Horse Racing Integration', description: 'Sync and import horse racing data' },
];

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [processingUserId, setProcessingUserId] = useState(null);
  const [resetModal, setResetModal] = useState({ open: false, userId: null, username: '' });
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetShowPw, setResetShowPw] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Permissions modal state
  const [permsModal, setPermsModal] = useState({ open: false, userId: null, username: '', role: '' });
  const [selectedPerms, setSelectedPerms] = useState([]);
  const [permsError, setPermsError] = useState('');
  const [permsSuccess, setPermsSuccess] = useState('');

  const navigate = useNavigate();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const endpoint = filter === 'all' ? '/management/users' : `/management/users?status=${filter}`;
      const response = await api.get(endpoint);
      setUsers(response.data.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      }
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApprove = async (userId) => {
    if (!window.confirm('Are you sure you want to approve this user?')) {
      return;
    }

    try {
      setProcessingUserId(userId);
      setError('');
      await api.post(`/management/approve-user/${userId}`);
      await fetchUsers(); // Refresh the list
      alert('User approved successfully. They will receive an email notification.');
    } catch (err) {
      console.error('Error approving user:', err);
      setError(err.response?.data?.error || 'Failed to approve user');
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleReject = async (userId) => {
    const reason = window.prompt('Please provide a reason for rejection (this will be sent to the user):');
    if (reason === null) {
      return; // User cancelled
    }

    try {
      setProcessingUserId(userId);
      setError('');
      await api.post(`/management/reject-user/${userId}`, { reason });
      await fetchUsers(); // Refresh the list
      alert('User rejected. They will receive an email notification with the reason.');
    } catch (err) {
      console.error('Error rejecting user:', err);
      setError(err.response?.data?.error || 'Failed to reject user');
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY DELETE this user? This action cannot be undone.')) {
      return;
    }

    try {
      setProcessingUserId(userId);
      setError('');
      await api.delete(`/management/delete-user/${userId}`);
      await fetchUsers(); // Refresh the list
      alert('User deleted successfully.');
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setProcessingUserId(null);
    }
  };

  const openResetModal = (userId, username) => {
    setResetModal({ open: true, userId, username });
    setResetPassword('');
    setResetConfirm('');
    setResetError('');
    setResetSuccess('');
  };

  const closeResetModal = () => {
    setResetModal({ open: false, userId: null, username: '' });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess('');

    if (resetPassword !== resetConfirm) {
      setResetError('Passwords do not match');
      return;
    }
    if (resetPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      return;
    }

    try {
      setProcessingUserId(resetModal.userId);
      await api.post(`/management/reset-user-password/${resetModal.userId}`, { newPassword: resetPassword });
      setResetSuccess('Password reset successfully.');
      setTimeout(() => closeResetModal(), 1500);
    } catch (err) {
      console.error('Error resetting password:', err);
      setResetError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setProcessingUserId(null);
    }
  };

  // Permission modal helpers
  const getComputedRole = (perms) => {
    if (!perms.length) return 'user';
    if (perms.length === 1 && perms[0] === 'manageTidbits') return 'contributor';
    return 'manager';
  };

  const openPermsModal = (targetUser) => {
    setPermsModal({ open: true, userId: targetUser.id, username: targetUser.username || targetUser.email, role: targetUser.role });
    setSelectedPerms(targetUser.permissions || []);
    setPermsError('');
    setPermsSuccess('');
  };

  const closePermsModal = () => {
    setPermsModal({ open: false, userId: null, username: '', role: '' });
  };

  const togglePermission = (key) => {
    setSelectedPerms(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);
  };

  const handleSavePermissions = async () => {
    setPermsError('');
    setPermsSuccess('');
    try {
      setProcessingUserId(permsModal.userId);
      await api.post(`/management/update-user-permissions/${permsModal.userId}`, { permissions: selectedPerms });
      const newRole = getComputedRole(selectedPerms);
      setPermsSuccess(`Saved. Role is now '${newRole}'.`);
      await fetchUsers();
      setTimeout(() => closePermsModal(), 1500);
    } catch (err) {
      console.error('Error updating permissions:', err);
      setPermsError(err.response?.data?.error || 'Failed to update permissions');
    } finally {
      setProcessingUserId(null);
    }
  };

  const handlePromoteToAdmin = async (userId, username) => {
    if (!window.confirm(`Promote ${username} to full admin?\n\nThis will grant them all admin permissions and full system access.`)) {
      return;
    }

    try {
      setProcessingUserId(userId);
      setError('');
      await api.post(`/management/promote-to-admin/${userId}`);
      await fetchUsers();
      alert(`${username} has been promoted to admin successfully.`);
    } catch (err) {
      console.error('Error promoting user to admin:', err);
      setError(err.response?.data?.error?.message || 'Failed to promote user to admin');
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleDemoteFromAdmin = async (userId, username) => {
    if (!window.confirm(`Demote ${username} from admin to regular user?\n\nThis will remove all admin permissions and system access.`)) {
      return;
    }

    try {
      setProcessingUserId(userId);
      setError('');
      await api.post(`/management/demote-from-admin/${userId}`);
      await fetchUsers();
      alert(`${username} has been demoted from admin successfully.`);
    } catch (err) {
      console.error('Error demoting user from admin:', err);
      setError(err.response?.data?.error?.message || 'Failed to demote user from admin');
    } finally {
      setProcessingUserId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status, rejectionReason) => {
    const statusClass = status === 'approved' ? 'status-approved' : 
                        status === 'pending' ? 'status-pending' : 'status-rejected';
    return (
      <span className={`status-badge ${statusClass}`} title={rejectionReason ? `Reason: ${rejectionReason}` : ''}>
        {status}
        {rejectionReason && ' ⓘ'}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const cls = role === 'admin' ? 'role-admin'
              : role === 'manager' ? 'role-manager'
              : role === 'contributor' ? 'role-contributor'
              : 'role-user';
    return <span className={`role-badge ${cls}`}>{role}</span>;
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1>User Management</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/admin')}>
          Back to Dashboard
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {/* Permissions Modal */}
      {permsModal.open && (
        <div className="modal-overlay">
          <div className="modal perms-modal">
            <h2>Manage Permissions</h2>
            <p>Configure elevated privileges for <strong>{permsModal.username}</strong>.</p>
            <p className="role-preview">
              Computed role: {
                (() => {
                  const r = getComputedRole(selectedPerms);
                  return <span className={`role-badge role-${r}`}>{r}</span>;
                })()
              }
            </p>
            {permsError && <p className="error">{permsError}</p>}
            {permsSuccess && <p className="success">{permsSuccess}</p>}
            <div className="perms-list">
              {PERMISSIONS.map(perm => (
                <label key={perm.key} className="perm-item">
                  <input
                    type="checkbox"
                    checked={selectedPerms.includes(perm.key)}
                    onChange={() => togglePermission(perm.key)}
                  />
                  <span className="perm-info">
                    <span className="perm-label">{perm.label}</span>
                    <span className="perm-desc">{perm.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={handleSavePermissions}
                disabled={processingUserId === permsModal.userId}
              >
                {processingUserId === permsModal.userId ? 'Saving...' : 'Save Permissions'}
              </button>
              <button className="btn btn-secondary" onClick={closePermsModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {resetModal.open && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Reset Password</h2>
            <p>Set a new password for <strong>{resetModal.username}</strong>.</p>
            {resetError && <p className="error">{resetError}</p>}
            {resetSuccess && <p className="success">{resetSuccess}</p>}
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label>New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={resetShowPw ? 'text' : 'password'}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    required
                    minLength="8"
                    placeholder="Min 8 characters"
                  />
                  <button type="button" className="pw-toggle" onClick={() => setResetShowPw(!resetShowPw)}>
                    {resetShowPw ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={resetShowPw ? 'text' : 'password'}
                    value={resetConfirm}
                    onChange={(e) => setResetConfirm(e.target.value)}
                    required
                    minLength="8"
                    placeholder="Repeat new password"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={processingUserId === resetModal.userId}>
                  {processingUserId === resetModal.userId ? 'Saving...' : 'Reset Password'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={closeResetModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="filter-section">
        <label>Filter by Status: </label>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
          <option value="all">All Users</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <p>Loading users...</p>
      ) : (
        <>
          <p className="user-count">Total: {users.length} user{users.length !== 1 ? 's' : ''}</p>
          
          <div className="table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Display Name</th>
                  <th>Status</th>
                  <th>Role</th>
                  <th>Registered</th>
                  <th>Approved At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{textAlign: 'center'}}>No users found</td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user.id}>
                      <td>{user.email}</td>
                      <td>{user.displayName}</td>
                      <td>{getStatusBadge(user.status, user.rejectionReason)}</td>
                      <td>{getRoleBadge(user.role)}</td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td>{formatDate(user.approvedAt)}</td>
                      <td>
                        {user.status === 'pending' && (
                          <div className="action-buttons">
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleApprove(user.id)}
                              disabled={processingUserId === user.id}
                            >
                              {processingUserId === user.id ? 'Processing...' : 'Approve'}
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleReject(user.id)}
                              disabled={processingUserId === user.id}
                            >
                              {processingUserId === user.id ? 'Processing...' : 'Reject'}
                            </button>
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => openResetModal(user.id, user.username || user.email)}
                              disabled={processingUserId === user.id}
                            >
                              Reset PW
                            </button>
                            <button
                              className="btn btn-purple btn-sm"
                              onClick={() => openPermsModal(user)}
                              disabled={processingUserId === user.id}
                            >
                              Permissions
                            </button>
                          </div>
                        )}
                        {user.status === 'rejected' && (
                          <div className="action-buttons">
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleApprove(user.id)}
                              disabled={processingUserId === user.id}
                            >
                              {processingUserId === user.id ? 'Processing...' : 'Re-approve'}
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDelete(user.id)}
                              disabled={processingUserId === user.id}
                            >
                              {processingUserId === user.id ? 'Processing...' : 'Delete'}
                            </button>
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => openResetModal(user.id, user.username || user.email)}
                              disabled={processingUserId === user.id}
                            >
                              Reset PW
                            </button>
                            <button
                              className="btn btn-purple btn-sm"
                              onClick={() => openPermsModal(user)}
                              disabled={processingUserId === user.id}
                            >
                              Permissions
                            </button>
                          </div>
                        )}
                        {user.status === 'approved' && user.role !== 'admin' && (
                          <div className="action-buttons">
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleReject(user.id)}
                              disabled={processingUserId === user.id}
                            >
                              {processingUserId === user.id ? 'Processing...' : 'Revoke'}
                            </button>
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => openResetModal(user.id, user.username || user.email)}
                              disabled={processingUserId === user.id}
                            >
                              Reset PW
                            </button>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handlePromoteToAdmin(user.id, user.username || user.email)}
                              disabled={processingUserId === user.id}
                            >
                              ⭐ Make Admin
                            </button>
                            <button
                              className="btn btn-purple btn-sm"
                              onClick={() => openPermsModal(user)}
                              disabled={processingUserId === user.id}
                            >
                              Permissions
                            </button>
                          </div>
                        )}
                        {user.status === 'approved' && user.role === 'admin' && (
                          <div className="action-buttons">
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => openResetModal(user.id, user.username || user.email)}
                              disabled={processingUserId === user.id}
                            >
                              Reset PW
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDemoteFromAdmin(user.id, user.username || user.email)}
                              disabled={processingUserId === user.id}
                            >
                              ⬇ Demote from Admin
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <style jsx="true">{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .filter-section {
          margin-bottom: 1.5rem;
        }

        .filter-select {
          padding: 0.5rem;
          margin-left: 0.5rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 1rem;
        }

        .user-count {
          margin-bottom: 1rem;
          color: #666;
        }

        .table-container {
          overflow-x: auto;
        }

        .users-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .users-table th,
        .users-table td {
          padding: 0.75rem;
          text-align: left;
          border-bottom: 1px solid #e0e0e0;
        }

        .users-table th {
          background-color: #f5f5f5;
          font-weight: 600;
          color: #333;
        }

        .users-table tbody tr:hover {
          background-color: #f9f9f9;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .status-pending {
          background-color: #fff3cd;
          color: #856404;
        }

        .status-approved {
          background-color: #d4edda;
          color: #155724;
        }

        .status-rejected {
          background-color: #f8d7da;
          color: #721c24;
        }

        .role-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .role-admin {
          background-color: #d1ecf1;
          color: #0c5460;
        }

        .role-manager {
          background-color: #e2d9f3;
          color: #4a235a;
        }

        .role-contributor {
          background-color: #d1f3e8;
          color: #0c5440;
        }

        .role-user {
          background-color: #e2e3e5;
          color: #383d41;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .btn-purple {
          background-color: #6f42c1;
          color: white;
        }

        .btn-purple:hover {
          background-color: #5a32a3;
        }

        .role-preview {
          margin-bottom: 1rem;
          font-size: 0.95rem;
        }

        .perms-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin: 1rem 0;
        }

        .perm-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          cursor: pointer;
          padding: 0.5rem;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          transition: background 0.15s;
        }

        .perm-item:hover {
          background-color: #f8f9fa;
        }

        .perm-item input[type="checkbox"] {
          width: 1.1rem;
          height: 1.1rem;
          margin-top: 0.15rem;
          flex-shrink: 0;
          cursor: pointer;
        }

        .perm-info {
          display: flex;
          flex-direction: column;
        }

        .perm-label {
          font-weight: 600;
          font-size: 0.95rem;
          color: #333;
        }

        .perm-desc {
          font-size: 0.8rem;
          color: #777;
          margin-top: 0.1rem;
        }

        .perms-modal {
          max-width: 520px;
          max-height: 85vh;
          overflow-y: auto;
        }

        .btn {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.3s ease;
        }

        .btn-sm {
          padding: 0.375rem 0.75rem;
          font-size: 0.8125rem;
        }

        .btn-primary {
          background-color: #007bff;
          color: white;
        }

        .btn-primary:hover {
          background-color: #0056b3;
        }

        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background-color: #545b62;
        }

        .btn-success {
          background-color: #28a745;
          color: white;
        }

        .btn-success:hover {
          background-color: #218838;
        }

        .btn-danger {
          background-color: #dc3545;
          color: white;
        }

        .btn-danger:hover {
          background-color: #c82333;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-warning {
          background-color: #fd7e14;
          color: white;
        }

        .btn-warning:hover {
          background-color: #e8590c;
        }

        .success {
          color: #155724;
          background-color: #d4edda;
          border: 1px solid #c3e6cb;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          margin-bottom: 0.5rem;
        }

        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: white;
          border-radius: 8px;
          padding: 2rem;
          min-width: 360px;
          max-width: 480px;
          width: 100%;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        }

        .modal h2 {
          margin-top: 0;
        }

        .modal .form-group {
          margin-bottom: 1rem;
        }

        .modal .form-group label {
          display: block;
          margin-bottom: 0.4rem;
          font-weight: 500;
        }

        .modal .form-group input {
          width: 100%;
          padding: 0.6rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 1rem;
          box-sizing: border-box;
        }

        .modal-actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 1.25rem;
        }

        .password-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .password-input-wrapper input {
          padding-right: 2.5rem;
        }

        .pw-toggle {
          position: absolute;
          right: 0.5rem;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1.1rem;
          line-height: 1;
          padding: 0;
        }
      `}</style>
    </div>
  );
}

export default UserManagement;
